<template>
  <div class="ai-mastering-page">
    <!-- Hero Section -->
    <section class="hero-section">
      <div class="container">
        <div class="hero-content">
          <div class="hero-text">
            <h1 class="hero-title">
              {{ $t('mastering.heroTitle') }}
            </h1>
            <p class="hero-subtitle">
              {{ $t('mastering.heroSubtitle') }}
            </p>
            <div class="hero-features">
              <div class="feature-item">
                <i class="fas fa-robot" />
                <span>{{ $t('mastering.feature1') }}</span>
              </div>
              <div class="feature-item">
                <i class="fas fa-headphones" />
                <span>{{ $t('mastering.feature2') }}</span>
              </div>
              <div class="feature-item">
                <i class="fas fa-download" />
                <span>{{ $t('mastering.feature3') }}</span>
              </div>
            </div>
            <div class="hero-pricing">
              <div class="price-tag">
                <span class="price-label">{{ $t('mastering.priceLabel') }}</span>
                <span class="price-amount">$15 {{ $t('mastering.perTrack') }}</span>
              </div>
            </div>
            <div class="hero-actions">
              <button
                class="btn btn-primary btn-large"
                @click="redirectToSignup"
              >
                <i class="fas fa-rocket" />
                {{ $t('mastering.getStarted') }}
              </button>
              <button
                class="btn btn-outline btn-large"
                @click="scrollToWorkflow"
              >
                <i class="fas fa-info-circle" />
                {{ $t('mastering.learnMore') }}
              </button>
            </div>
          </div>
          <div class="hero-media">
            <div class="hero-visual">
              <div class="audio-visualizer">
                <div class="waveform">
                  <div
                    v-for="(height, index) in barHeights"
                    :key="index"
                    class="bar"
                    :style="{
                      animationDelay: `${index * 0.05}s`,
                      height: `${height}%`,
                      transition: 'height 0.5s ease'
                    }"
                  />
                </div>
                <div class="ai-badge">
                  <i class="fas fa-brain" />
                  <span>AI Powered</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- How It Works Section -->
    <section
      ref="workflowSection"
      class="workflow-section"
    >
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">
            {{ $t('mastering.workflowTitle') }}
          </h2>
          <p class="section-subtitle">
            {{ $t('mastering.workflowSubtitle') }}
          </p>
        </div>

        <div class="workflow-steps">
          <div
            v-for="(step, index) in workflowSteps"
            :key="index"
            class="workflow-step"
          >
            <div class="step-number">
              {{ index + 1 }}
            </div>
            <div class="step-content">
              <div class="step-icon">
                <i :class="step.icon" />
              </div>
              <h3 class="step-title">
                {{ $t(step.title) }}
              </h3>
              <p class="step-description">
                {{ $t(step.description) }}
              </p>
              <div
                v-if="step.extra"
                class="step-extra"
              >
                {{ $t(step.extra) }}
              </div>
            </div>
            <div
              v-if="index < workflowSteps.length - 1"
              class="step-arrow"
            >
              <i class="fas fa-arrow-right" />
            </div>
          </div>
        </div>

        <div class="workflow-demo">
          <h3 class="demo-title">
            {{ $t('mastering.demoTitle') }}
          </h3>
          <div class="demo-content">
            <div class="demo-presets">
              <div
                v-for="(preset, index) in masteringPresets"
                :key="index"
                class="preset-card"
                :class="{ active: selectedPreset === index }"
                @click="selectPreset(index)"
              >
                <div class="preset-icon">
                  <i :class="preset.icon" />
                </div>
                <h4 class="preset-name">
                  {{ $t(preset.name) }}
                </h4>
                <p class="preset-description">
                  {{ $t(preset.description) }}
                </p>
                <div class="preset-features">
                  <span
                    v-for="(feature, idx) in preset.features"
                    :key="idx"
                    class="feature-tag"
                  >
                    {{ $t(feature) }}
                  </span>
                </div>
              </div>
            </div>
            <div class="demo-notice">
              <div class="notice-content">
                <i class="fas fa-info-circle" />
                <div class="notice-text">
                  <h4>{{ $t('mastering.demoNoticeTitle') }}</h4>
                  <p>{{ $t('mastering.demoNoticeText') }}</p>
                </div>
              </div>
              <button
                class="btn btn-primary"
                @click="redirectToSignup"
              >
                <i class="fas fa-upload" />
                {{ $t('mastering.startMastering') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <section class="features-section">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">
            {{ $t('mastering.featuresTitle') }}
          </h2>
          <p class="section-subtitle">
            {{ $t('mastering.featuresSubtitle') }}
          </p>
        </div>

        <div class="features-grid">
          <div
            v-for="(feature, index) in features"
            :key="index"
            class="feature-card"
          >
            <div class="feature-icon">
              <i :class="feature.icon" />
            </div>
            <h3 class="feature-title">
              {{ $t(feature.title) }}
            </h3>
            <p class="feature-description">
              {{ $t(feature.description) }}
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Pricing Section -->
    <section class="pricing-section">
      <div class="container">
        <div class="pricing-card">
          <div class="pricing-header">
            <h2 class="pricing-title">
              {{ $t('mastering.pricingTitle') }}
            </h2>
            <div class="pricing-amount">
              <span class="currency">$</span>
              <span class="amount">15</span>
              <span class="per-track">{{ $t('mastering.perTrack') }}</span>
            </div>
          </div>
          <div class="pricing-features">
            <div
              v-for="(feature, index) in pricingFeatures"
              :key="index"
              class="pricing-feature"
            >
              <i class="fas fa-check" />
              <span>{{ $t(feature) }}</span>
            </div>
          </div>
          <div class="pricing-actions">
            <button
              class="btn btn-primary btn-xl"
              @click="redirectToSignup"
            >
              <i class="fas fa-credit-card" />
              {{ $t('mastering.payAndMaster') }}
            </button>
            <p class="pricing-note">
              {{ $t('mastering.pricingNote') }}
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="cta-section">
      <div class="container">
        <div class="cta-content">
          <h2 class="cta-title">
            {{ $t('mastering.ctaTitle') }}
          </h2>
          <p class="cta-subtitle">
            {{ $t('mastering.ctaSubtitle') }}
          </p>
          <div class="cta-actions">
            <button
              class="btn btn-primary btn-xl"
              @click="redirectToSignup"
            >
              <i class="fas fa-upload" />
              {{ $t('mastering.uploadTrack') }}
            </button>
            <button
              class="btn btn-outline btn-xl"
              @click="scrollToWorkflow"
            >
              <i class="fas fa-question-circle" />
              {{ $t('mastering.howItWorks') }}
            </button>
          </div>
          <div class="cta-guarantee">
            <i class="fas fa-shield-check" />
            {{ $t('mastering.qualityGuarantee') }}
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { usePageSeo } from '@/composables/usePageSeo';

export default {
  name: 'AIMasteringView',
  setup() {
    const { t } = useI18n();
    const router = useRouter();
    const workflowSection = ref(null);
    const selectedPreset = ref(0);

    usePageSeo({
      titleKey: 'mastering.seoTitle',
      descriptionKey: 'mastering.seoDescription',
    });

    // Workflow steps data
    const workflowSteps = ref([
      {
        icon: 'fas fa-upload',
        title: 'mastering.step1Title',
        description: 'mastering.step1Description',
        extra: 'mastering.step1Extra',
      },
      {
        icon: 'fas fa-sliders-h',
        title: 'mastering.step2Title',
        description: 'mastering.step2Description',
        extra: 'mastering.step2Extra',
      },
      {
        icon: 'fas fa-magic',
        title: 'mastering.step3Title',
        description: 'mastering.step3Description',
        extra: 'mastering.step3Extra',
      },
      {
        icon: 'fas fa-download',
        title: 'mastering.step4Title',
        description: 'mastering.step4Description',
        extra: 'mastering.step4Extra',
      },
    ]);

    // Mastering presets data
    const masteringPresets = ref([
      {
        icon: 'fas fa-volume-up',
        name: 'mastering.presets.balanced.name',
        description: 'mastering.presets.balanced.description',
        features: [
          'mastering.presets.balanced.feature1',
          'mastering.presets.balanced.feature2',
          'mastering.presets.balanced.feature3',
        ],
      },
      {
        icon: 'fas fa-fire',
        name: 'mastering.presets.loud.name',
        description: 'mastering.presets.loud.description',
        features: [
          'mastering.presets.loud.feature1',
          'mastering.presets.loud.feature2',
          'mastering.presets.loud.feature3',
        ],
      },
      {
        icon: 'fas fa-leaf',
        name: 'mastering.presets.dynamic.name',
        description: 'mastering.presets.dynamic.description',
        features: [
          'mastering.presets.dynamic.feature1',
          'mastering.presets.dynamic.feature2',
          'mastering.presets.dynamic.feature3',
        ],
      },
    ]);

    // Features data
    const features = ref([
      {
        icon: 'fas fa-brain',
        title: 'mastering.features.ai.title',
        description: 'mastering.features.ai.description',
      },
      {
        icon: 'fas fa-bolt',
        title: 'mastering.features.fast.title',
        description: 'mastering.features.fast.description',
      },
      {
        icon: 'fas fa-award',
        title: 'mastering.features.professional.title',
        description: 'mastering.features.professional.description',
      },
      {
        icon: 'fas fa-chart-line',
        title: 'mastering.features.analysis.title',
        description: 'mastering.features.analysis.description',
      },
      {
        icon: 'fas fa-music',
        title: 'mastering.features.formats.title',
        description: 'mastering.features.formats.description',
      },
      {
        icon: 'fas fa-shield-check',
        title: 'mastering.features.quality.title',
        description: 'mastering.features.quality.description',
      },
    ]);

    // Pricing features
    const pricingFeatures = ref([
      'mastering.pricing.feature1',
      'mastering.pricing.feature2',
      'mastering.pricing.feature3',
      'mastering.pricing.feature4',
      'mastering.pricing.feature5',
      'mastering.pricing.feature6',
    ]);

    // Function to redirect to signup
    const redirectToSignup = () => {
      router.push({ name: 'tools' });
    };

    // Function to scroll to workflow
    const scrollToWorkflow = () => {
      if (workflowSection.value) {
        workflowSection.value.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    };

    // Function to select preset
    const selectPreset = (index) => {
      selectedPreset.value = index;
    };

    // Generate random heights for waveform bars
    const generateBarHeights = () => {
      return Array.from({ length: 32 }, () => Math.random() * 60 + 40);
    };

    const barHeights = ref(generateBarHeights());

    onMounted(() => {
      setInterval(() => {
        barHeights.value = generateBarHeights();
      }, 3000);
    });

    return {
      workflowSteps,
      masteringPresets,
      features,
      pricingFeatures,
      workflowSection,
      selectedPreset,
      barHeights,
      redirectToSignup,
      scrollToWorkflow,
      selectPreset,
    };
  },
};
</script>

<style lang="scss" scoped>
@import "@/styles/abstracts/colors";
@import "@/styles/abstracts/breakpoints";

.ai-mastering-page {
  min-height: 100vh;

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 1rem;

    @media (max-width: $mobile) {
      padding: 0 0.75rem;
    }
  }
}

// Hero Section
.hero-section {
  min-height: 100vh;
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(102, 126, 234, 0.9) 0%, rgba(118, 75, 162, 0.9) 100%);
    z-index: 1;
  }

  .container {
    position: relative;
    z-index: 2;
    padding: 0 1rem;

    @media (max-width: $mobile) {
      padding: 0 0.75rem;
    }
  }

  .hero-content {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4rem;
    align-items: center;

    @media (max-width: $tablet) {
      grid-template-columns: 1fr;
      gap: 2rem;
      text-align: center;
      padding: 2rem 0;
    }

    @media (max-width: $mobile) {
      gap: 1.5rem;
      padding: 1rem 0;
    }
  }

  .hero-text {
    color: white;

    .hero-title {
      font-size: 3.5rem;
      font-weight: 800;
      line-height: 1.1;
      margin-bottom: 1.5rem;

      @media (max-width: $tablet) {
        font-size: 2.5rem;
        margin-bottom: 1rem;
      }

      @media (max-width: $mobile) {
        font-size: 1.8rem;
        line-height: 1.2;
        margin-bottom: 0.75rem;
      }
    }

    .hero-subtitle {
      font-size: 1.25rem;
      line-height: 1.6;
      margin-bottom: 2rem;
      opacity: 0.9;

      @media (max-width: $tablet) {
        margin-bottom: 1.5rem;
      }

      @media (max-width: $mobile) {
        font-size: 1rem;
        line-height: 1.5;
        margin-bottom: 1rem;
      }
    }

    .hero-features {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      margin-bottom: 2rem;

      @media (max-width: $tablet) {
        margin-bottom: 1.5rem;
        gap: 0.75rem;
      }

      @media (max-width: $mobile) {
        margin-bottom: 1rem;
        gap: 0.5rem;
      }

      .feature-item {
        display: flex;
        align-items: center;
        gap: 1rem;

        @media (max-width: $mobile) {
          gap: 0.75rem;
          justify-content: center;
        }

        i {
          font-size: 1.2rem;
          color: #ffd700;
          width: 20px;

          @media (max-width: $mobile) {
            font-size: 1rem;
            width: 16px;
          }
        }

        span {
          font-size: 1rem;
          opacity: 0.9;

          @media (max-width: $mobile) {
            font-size: 0.9rem;
          }
        }
      }
    }

    .hero-pricing {
      margin-bottom: 2.5rem;

      @media (max-width: $tablet) {
        margin-bottom: 2rem;
      }

      @media (max-width: $mobile) {
        margin-bottom: 1.5rem;
      }

      .price-tag {
        background: rgba(255, 255, 255, 0.15);
        backdrop-filter: blur(10px);
        border-radius: 50px;
        padding: 1rem 2rem;
        display: inline-flex;
        align-items: center;
        gap: 1rem;
        border: 1px solid rgba(255, 255, 255, 0.2);

        @media (max-width: $mobile) {
          padding: 0.75rem 1.5rem;
          gap: 0.75rem;
          border-radius: 40px;
        }

        .price-label {
          font-size: 0.9rem;
          opacity: 0.8;

          @media (max-width: $mobile) {
            font-size: 0.8rem;
          }
        }

        .price-amount {
          font-size: 1.8rem;
          font-weight: 700;
          color: #ffd700;

          @media (max-width: $mobile) {
            font-size: 1.5rem;
          }
        }
      }
    }

    .hero-actions {
      display: flex;
      gap: 1rem;

      @media (max-width: $tablet) {
        gap: 0.75rem;
      }

      @media (max-width: $mobile) {
        flex-direction: column;
        gap: 0.75rem;
        width: 100%;
      }
    }
  }

  .hero-media {
    .hero-visual {
      display: flex;
      justify-content: center;
      align-items: center;

      .audio-visualizer {
        background: rgba(255, 255, 255, 0.1);
        padding: 3rem;
        border-radius: 20px;
        backdrop-filter: blur(10px);
        border: 1px solid rgba(255, 255, 255, 0.2);
        position: relative;

        @media (max-width: $tablet) {
          padding: 2rem;
          border-radius: 15px;
        }

        @media (max-width: $mobile) {
          padding: 1.5rem;
          border-radius: 12px;
          max-width: 300px;
          width: 100%;
        }

        .waveform {
          display: flex;
          align-items: end;
          gap: 4px;
          height: 120px;
          justify-content: center;
          padding: 1rem 0;

          @media (max-width: $tablet) {
            height: 100px;
            gap: 3px;
          }

          @media (max-width: $mobile) {
            height: 80px;
            gap: 2px;
            padding: 0.75rem 0;
          }

          .bar {
            width: 8px;
            height: 100%;
            min-height: 30px;
            background: linear-gradient(to top, #ffd700 0%, #ff6b6b 50%, #667eea 100%);
            border-radius: 4px;
            animation: wave 2s ease-in-out infinite;
            transform-origin: bottom;
            opacity: 1;
            box-shadow: 0 4px 12px rgba(255, 215, 0, 0.4);
            border: 1px solid rgba(255, 255, 255, 0.2);

            @media (max-width: $tablet) {
              width: 6px;
              min-height: 25px;
              border-radius: 3px;
            }

            @media (max-width: $mobile) {
              width: 4px;
              min-height: 20px;
              border-radius: 2px;
              box-shadow: 0 2px 8px rgba(255, 215, 0, 0.3);
            }
          }

          @keyframes wave {
            0% {
              transform: scaleY(0.3);
              opacity: 0.7;
              filter: brightness(0.8);
            }
            25% {
              transform: scaleY(0.9);
              opacity: 1;
              filter: brightness(1.2);
            }
            50% {
              transform: scaleY(1.2);
              opacity: 1;
              filter: brightness(1.4);
            }
            75% {
              transform: scaleY(0.7);
              opacity: 0.9;
              filter: brightness(1);
            }
            100% {
              transform: scaleY(0.3);
              opacity: 0.7;
              filter: brightness(0.8);
            }
          }
        }

        .ai-badge {
          position: absolute;
          top: -10px;
          right: -10px;
          background: #ffd700;
          color: #2d3748;
          padding: 0.5rem 1rem;
          border-radius: 50px;
          font-size: 0.9rem;
          font-weight: 600;
          box-shadow: 0 4px 12px rgba(255, 215, 0, 0.3);
          display: flex;
          align-items: center;
          gap: 0.5rem;

          i {
            font-size: 1rem;
          }
        }
      }
    }
  }
}

// Workflow Section
.workflow-section {
  padding: 8rem 0;
  background: #f8fafc;

  @media (max-width: $tablet) {
    padding: 6rem 0;
  }

  @media (max-width: $mobile) {
    padding: 4rem 0;
  }

  .container {
    padding: 0 1rem;

    @media (max-width: $mobile) {
      padding: 0 0.75rem;
    }
  }

  .section-header {
    text-align: center;
    margin-bottom: 6rem;

    @media (max-width: $tablet) {
      margin-bottom: 4rem;
    }

    @media (max-width: $mobile) {
      margin-bottom: 3rem;
    }

    .section-title {
      font-size: 2.5rem;
      font-weight: 700;
      color: #2d3748;
      margin-bottom: 1rem;

      @media (max-width: $tablet) {
        font-size: 2rem;
      }

      @media (max-width: $mobile) {
        font-size: 1.6rem;
        margin-bottom: 0.75rem;
      }
    }

    .section-subtitle {
      font-size: 1.2rem;
      color: #718096;
      max-width: 600px;
      margin: 0 auto;

      @media (max-width: $mobile) {
        font-size: 1rem;
        max-width: 100%;
        padding: 0 1rem;
      }
    }
  }

  .workflow-steps {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 6rem;
    position: relative;

    @media (max-width: $tablet) {
      flex-direction: column;
      gap: 3rem;
      margin-bottom: 4rem;
    }

    @media (max-width: $mobile) {
      gap: 2rem;
      margin-bottom: 3rem;
    }

    .workflow-step {
      flex: 1;
      text-align: center;
      position: relative;
      padding: 0 1rem;

      @media (max-width: $mobile) {
        padding: 0 0.5rem;
      }

      .step-number {
        width: 60px;
        height: 60px;
        background: linear-gradient(135deg, #667eea, #764ba2);
        color: white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.5rem;
        font-weight: 700;
        margin: 0 auto 1.5rem;
        position: relative;
        z-index: 2;

        @media (max-width: $mobile) {
          width: 50px;
          height: 50px;
          font-size: 1.2rem;
          margin-bottom: 1rem;
        }
      }

      .step-content {
        .step-icon {
          width: 80px;
          height: 80px;
          background: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);

          @media (max-width: $mobile) {
            width: 60px;
            height: 60px;
            margin-bottom: 1rem;
          }

          i {
            font-size: 2rem;
            color: #667eea;

            @media (max-width: $mobile) {
              font-size: 1.5rem;
            }
          }
        }

        .step-title {
          font-size: 1.3rem;
          font-weight: 600;
          color: #2d3748;
          margin-bottom: 1rem;

          @media (max-width: $mobile) {
            font-size: 1.1rem;
            margin-bottom: 0.75rem;
          }
        }

        .step-description {
          color: #718096;
          line-height: 1.6;
          margin-bottom: 1rem;

          @media (max-width: $mobile) {
            font-size: 0.9rem;
            line-height: 1.5;
            margin-bottom: 0.75rem;
          }
        }

        .step-extra {
          font-size: 0.9rem;
          color: #667eea;
          font-weight: 600;

          @media (max-width: $mobile) {
            font-size: 0.8rem;
          }
        }
      }

      .step-arrow {
        position: absolute;
        top: 30px;
        right: -20px;
        color: #cbd5e0;
        font-size: 1.5rem;

        @media (max-width: $tablet) {
          display: none;
        }
      }
    }
  }

  .workflow-demo {
    background: white;
    border-radius: 20px;
    padding: 3rem;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);

    @media (max-width: $tablet) {
      padding: 2rem;
      border-radius: 15px;
    }

    @media (max-width: $mobile) {
      padding: 1.5rem;
      border-radius: 12px;
      margin: 0 0.5rem;
    }

    .demo-title {
      font-size: 2rem;
      font-weight: 700;
      color: #2d3748;
      text-align: center;
      margin-bottom: 3rem;

      @media (max-width: $tablet) {
        font-size: 1.6rem;
        margin-bottom: 2rem;
      }

      @media (max-width: $mobile) {
        font-size: 1.4rem;
        margin-bottom: 1.5rem;
      }
    }

    .demo-content {
      display: grid;
      grid-template-columns: 2fr 1fr;
      gap: 3rem;
      align-items: start;

      @media (max-width: $tablet) {
        grid-template-columns: 1fr;
        gap: 2rem;
      }

      @media (max-width: $mobile) {
        gap: 1.5rem;
      }

      .demo-presets {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
        gap: 1.5rem;

        @media (max-width: $mobile) {
          grid-template-columns: 1fr;
          gap: 1rem;
        }

        .preset-card {
          background: #f8fafc;
          border-radius: 15px;
          padding: 1.5rem;
          text-align: center;
          cursor: pointer;
          transition: all 0.3s ease;
          border: 2px solid transparent;

          &:hover {
            transform: translateY(-5px);
            box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
          }

          &.active {
            border-color: #667eea;
            background: linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1));
          }

          .preset-icon {
            width: 60px;
            height: 60px;
            background: linear-gradient(135deg, #667eea, #764ba2);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 1rem;

            i {
              font-size: 1.5rem;
              color: white;
            }
          }

          .preset-name {
            font-size: 1.2rem;
            font-weight: 600;
            color: #2d3748;
            margin-bottom: 0.5rem;
          }

          .preset-description {
            color: #718096;
            font-size: 0.9rem;
            margin-bottom: 1rem;
            line-height: 1.5;
          }

          .preset-features {
            display: flex;
            flex-wrap: wrap;
            gap: 0.5rem;
            justify-content: center;

            .feature-tag {
              background: rgba(102, 126, 234, 0.1);
              color: #667eea;
              padding: 0.25rem 0.75rem;
              border-radius: 50px;
              font-size: 0.8rem;
              font-weight: 500;
            }
          }
        }
      }

      .demo-notice {
        background: #f8fafc;
        border-radius: 15px;
        padding: 2rem;
        border-left: 4px solid #667eea;

        .notice-content {
          display: flex;
          gap: 1rem;
          margin-bottom: 1.5rem;

          i {
            font-size: 1.5rem;
            color: #667eea;
            margin-top: 0.2rem;
            flex-shrink: 0;
          }

          .notice-text {
            h4 {
              font-size: 1.2rem;
              font-weight: 600;
              color: #2d3748;
              margin-bottom: 0.5rem;
            }

            p {
              color: #718096;
              line-height: 1.6;
            }
          }
        }
      }
    }
  }
}

// Features Section
.features-section {
  padding: 8rem 0;
  background: white;

  @media (max-width: $tablet) {
    padding: 6rem 0;
  }

  @media (max-width: $mobile) {
    padding: 4rem 0;
  }

  .container {
    padding: 0 1rem;

    @media (max-width: $mobile) {
      padding: 0 0.75rem;
    }
  }

  .section-header {
    text-align: center;
    margin-bottom: 6rem;

    @media (max-width: $tablet) {
      margin-bottom: 4rem;
    }

    @media (max-width: $mobile) {
      margin-bottom: 3rem;
    }

    .section-title {
      font-size: 2.5rem;
      font-weight: 700;
      color: #2d3748;
      margin-bottom: 1rem;

      @media (max-width: $tablet) {
        font-size: 2rem;
      }

      @media (max-width: $mobile) {
        font-size: 1.6rem;
        margin-bottom: 0.75rem;
      }
    }

    .section-subtitle {
      font-size: 1.2rem;
      color: #718096;
      max-width: 600px;
      margin: 0 auto;

      @media (max-width: $mobile) {
        font-size: 1rem;
        max-width: 100%;
        padding: 0 1rem;
      }
    }
  }

  .features-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 3rem;

    @media (max-width: $tablet) {
      grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
      gap: 2rem;
    }

    @media (max-width: $mobile) {
      grid-template-columns: 1fr;
      gap: 1.5rem;
    }

    .feature-card {
      text-align: center;
      padding: 2rem;
      background: #f8fafc;
      border-radius: 15px;
      transition: transform 0.3s ease, box-shadow 0.3s ease;

      @media (max-width: $mobile) {
        padding: 1.5rem;
        border-radius: 12px;
      }

      &:hover {
        transform: translateY(-5px);
        box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
      }

      .feature-icon {
        width: 80px;
        height: 80px;
        background: linear-gradient(135deg, #667eea, #764ba2);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto 1.5rem;

        @media (max-width: $mobile) {
          width: 60px;
          height: 60px;
          margin-bottom: 1rem;
        }

        i {
          font-size: 2rem;
          color: white;

          @media (max-width: $mobile) {
            font-size: 1.5rem;
          }
        }
      }

      .feature-title {
        font-size: 1.3rem;
        font-weight: 600;
        color: #2d3748;
        margin-bottom: 1rem;

        @media (max-width: $mobile) {
          font-size: 1.1rem;
          margin-bottom: 0.75rem;
        }
      }

      .feature-description {
        color: #718096;
        line-height: 1.6;

        @media (max-width: $mobile) {
          font-size: 0.9rem;
          line-height: 1.5;
        }
      }
    }
  }
}

// Pricing Section
.pricing-section {
  padding: 8rem 0;
  background: linear-gradient(135deg, #f8fafc 0%, #edf2f7 100%);

  @media (max-width: $tablet) {
    padding: 6rem 0;
  }

  @media (max-width: $mobile) {
    padding: 4rem 0;
  }

  .container {
    padding: 0 1rem;

    @media (max-width: $mobile) {
      padding: 0 0.75rem;
    }
  }

  .pricing-card {
    background: white;
    border-radius: 20px;
    padding: 3rem;
    text-align: center;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
    max-width: 500px;
    margin: 0 auto;
    border: 2px solid #667eea;

    @media (max-width: $tablet) {
      padding: 2.5rem;
      border-radius: 15px;
    }

    @media (max-width: $mobile) {
      padding: 2rem;
      border-radius: 12px;
      margin: 0 0.5rem;
      max-width: 100%;
    }

    .pricing-header {
      margin-bottom: 2rem;

      .pricing-title {
        font-size: 2rem;
        font-weight: 700;
        color: #2d3748;
        margin-bottom: 1rem;
      }

      .pricing-amount {
        display: flex;
        align-items: baseline;
        justify-content: center;
        gap: 0.25rem;

        .currency {
          font-size: 2rem;
          font-weight: 600;
          color: #667eea;
        }

        .amount {
          font-size: 4rem;
          font-weight: 800;
          color: #667eea;
        }

        .per-track {
          font-size: 1.2rem;
          color: #718096;
          font-weight: 500;
        }
      }
    }

    .pricing-features {
      margin-bottom: 3rem;

      .pricing-feature {
        display: flex;
        align-items: center;
        gap: 1rem;
        margin-bottom: 1rem;
        justify-content: flex-start;
        text-align: left;

        i {
          color: #48bb78;
          font-size: 1.1rem;
          width: 20px;
          flex-shrink: 0;
        }

        span {
          color: #4a5568;
          line-height: 1.5;
        }
      }
    }

    .pricing-actions {
      .pricing-note {
        margin-top: 1rem;
        font-size: 0.9rem;
        color: #718096;
        font-style: italic;
      }
    }
  }
}

// CTA Section
.cta-section {
  padding: 8rem 0;
  background: linear-gradient(135deg, #2d3748 0%, #4a5568 100%);
  color: white;
  text-align: center;

  @media (max-width: $tablet) {
    padding: 6rem 0;
  }

  @media (max-width: $mobile) {
    padding: 4rem 0;
  }

  .container {
    padding: 0 1rem;

    @media (max-width: $mobile) {
      padding: 0 0.75rem;
    }
  }

  .cta-title {
    font-size: 2.5rem;
    font-weight: 700;
    margin-bottom: 1rem;

    @media (max-width: $tablet) {
      font-size: 2rem;
    }

    @media (max-width: $mobile) {
      font-size: 1.6rem;
      margin-bottom: 0.75rem;
    }
  }

  .cta-subtitle {
    font-size: 1.2rem;
    opacity: 0.9;
    margin-bottom: 3rem;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;

    @media (max-width: $tablet) {
      margin-bottom: 2.5rem;
    }

    @media (max-width: $mobile) {
      font-size: 1rem;
      margin-bottom: 2rem;
      max-width: 100%;
      padding: 0 1rem;
    }
  }

  .cta-actions {
    display: flex;
    justify-content: center;
    gap: 1.5rem;
    margin-bottom: 2rem;

    @media (max-width: $tablet) {
      gap: 1rem;
    }

    @media (max-width: $mobile) {
      flex-direction: column;
      align-items: center;
      gap: 0.75rem;
      margin-bottom: 1.5rem;
    }
  }

  .cta-guarantee {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    opacity: 0.8;
    font-size: 0.9rem;

    @media (max-width: $mobile) {
      font-size: 0.85rem;
      gap: 0.4rem;
    }

    i {
      color: #48bb78;
    }
  }
}

// Button Styles
.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 2rem;
  border: none;
  border-radius: 50px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 1rem;
  justify-content: center;

  @media (max-width: $mobile) {
    width: 100%;
    padding: 0.9rem 1.5rem;
    font-size: 0.95rem;
    gap: 0.4rem;
  }

  &.btn-large {
    padding: 1.2rem 2.5rem;
    font-size: 1.1rem;

    @media (max-width: $mobile) {
      padding: 1rem 2rem;
      font-size: 1rem;
    }
  }

  &.btn-xl {
    padding: 1.5rem 3rem;
    font-size: 1.2rem;

    @media (max-width: $mobile) {
      padding: 1.2rem 2rem;
      font-size: 1.1rem;
    }
  }

  &.btn-primary {
    background: linear-gradient(135deg, #667eea, #764ba2);
    color: white;
    box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
    }
  }

  &.btn-outline {
    background: transparent;
    color: white;
    border: 2px solid rgba(255, 255, 255, 0.3);

    &:hover {
      background: rgba(255, 255, 255, 0.1);
      border-color: rgba(255, 255, 255, 0.5);
    }
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none !important;
  }
}
</style>
